import Queue from './Queue'
import LogTool from "../logger/logTools";

export interface ActionHistoryCallable {
  fn: (...args: any[]) => unknown
  args: unknown[]
}
export interface ActionHistoryEntry {
  undo: ActionHistoryCallable,
  redo: ActionHistoryCallable,
}
export interface ActionHistoryConfig {
  maxHistoryLength?: number // this limits how many entries are stored in the
                            // action history. A user can chain at most maxHistoryLength
                            // number of undo calls.
}



const log = new LogTool({ context: 'ActionHistory', enable: true, logLevel: 'warn'})

/**
 * Experimental!
 *
 * A class to manage undo/redo functionality for any kind of actions.
 * How to use:
 *  1. Create a new instance of ActionHistory.
 *  2. Call history.push() within an action that you want to be undoable.
 *    - provide a function that undoes the action and a function that re-does it.
 *    - make sure to also provide the required arguments for both functions.
 *  3. Call history.undo() or history.redo() to execute the undo/redo action.
 */
export default class ActionHistory {
  private _config: ActionHistoryConfig = {}
  private _history: Queue<ActionHistoryEntry>
  // index to keep track of at which position of the history we are currently at
  private _historyPosition = -1
  // the name of the function that was called by undo or redo. We keep track of this
  // to be able to tell whether a push operation was triggered by an undo or redo call
  private _calledByUndoOrRedo: string | undefined = undefined

  /**
   * Returns true when the current push operation was triggered by an undo or redo call.
   * @private
   */
  private get _isPushTriggeredByUndoOrRedo(): boolean {
    const stackTrace = new Error().stack?.split('\n')
    if(!stackTrace || !this._calledByUndoOrRedo) return false
    log.debug('Stack trace ->', stackTrace)

    // check whether the undo or redo method appears on the stack trace
    let undoOrRedoIndex: number = stackTrace?.findIndex(
      // ... check for undo
      line => line.includes('undo')
    )
    if(undoOrRedoIndex === -1) {
      // ... undo was not found -> check for redo
      undoOrRedoIndex = stackTrace?.findIndex(line => line.includes('redo'))
    }
    // no appearance? Then push was not triggered by undo or redo
    if(undoOrRedoIndex === -1) return false
    log.debug('Found undo/redo call at index ->', undoOrRedoIndex)

    // the stack trace contains an undo/redo call. If the stack trace matches
    // the name of the function that was last called by undo or redo, then the push
    // must have been triggered by an undo or redo call.
    return stackTrace[undoOrRedoIndex - 1].includes(this._calledByUndoOrRedo);

  }

  constructor(config?: ActionHistoryConfig) {
    // validate config
    if(config?.maxHistoryLength && config.maxHistoryLength < 1) {
      throw new Error('The maxHistoryLength setting must be greater than 1.')
    }
    // write config
    this._config = {
      maxHistoryLength: config?.maxHistoryLength || 20
    }
    // initialize action history
    this._history = new Queue<ActionHistoryEntry>({
      maxSize: this._config.maxHistoryLength,
      overflow: true,
    })
  }

  /**
   * Adds a new entry to the action history.
   * @param historyEntry.undo A function that, when executed, undoes whatever
   *  action you are currently performing.
   * @param historyEntry.redo A function that, when executed, re-does whatever
   *  action you are currently performing.
   */
  push(historyEntry: ActionHistoryEntry): void {
    // make sure that we do not push a new history entry as a side effect of executing
    // an undo or redo action
    if(this._isPushTriggeredByUndoOrRedo) {
      log.info('Registered a push call that was triggered by undo/redo -> Do nothing')
      return
    }

    if(this._historyPosition < this._history.length - 1) {
      // we currently are somewhere in the middle of the action history. By adding a
      // new entry in the middle, the 'future' of the history is overwritten
      this._history = new Queue(
        this._history.getAll().slice(0, this._historyPosition + 1),
        {maxSize: this._config.maxHistoryLength, overflow: true}
      )
    }
    this._history.enqueue(historyEntry)
    this._historyPosition = this._history.length - 1
  }

  /**
   * Executes the undo action registered at the current history position.
   *
   * @throws Error if the current history position is already at the beginning of
   *  the action history. Make sure to catch this error and provide useful visual
   *  feedback to the user.
   */
  undo(): unknown {
    if(this._historyPosition <= -1) {
      throw new Error(
        'Reached the end of the action history. There is nothing left to undo. '
        + 'You should catch this error and maybe provide some visual feedback to the user.'
      )
    }
    // get the current history position
    const {undo} = this._history.get(this._historyPosition)
    // update history position
    this._historyPosition --
    // register function name in _calledByUndoOrRedo
    this._calledByUndoOrRedo = undo.fn.name
    // undo ...
    log.info(`Executing ${undo.fn.name}() with args: `, ...undo.args)
    return undo.fn(...undo.args)
  }

  /**
   * Executes the redo action registered at the next history position.
   *
   * @throws Error if the next history position is already at the end of the
   *  action history. Make sure to catch this error and provide useful visual
   *  feedback to the user.
   */
  redo(): unknown {
    if(this._historyPosition >= this._history.length - 1) {
      throw new Error(
        'Reached the end of the action history. There is nothing left to redo. '
        + 'You should catch this error and maybe provide some visual feedback to the user.'
      )
    }
    // get the next history position
    const {redo} = this._history.get(this._historyPosition + 1)
    // update history position
    this._historyPosition ++
    // register function name in _calledByUndoOrRedo
    this._calledByUndoOrRedo = redo.fn.name
    // redo ...
    log.info(`Executing ${redo.fn.name}() with args: `, ...redo.args)
    return redo.fn(...redo.args)
  }
}